import type { AnyExtension, Extension } from '@tiptap/core';
import { Heading } from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '@tiptap/extension-table';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { type Editor, type Extensions, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import { Markdown } from 'tiptap-markdown';
import { LinkWithState } from './link-with-state';

/**
 * Tiptap extensions used by the read-only markdown preview surfaces
 * (reader, CR diff preview). Mirrors docEditorExtensions minus the
 * block-marker authoring extension.
 */
export const docPreviewExtensions: AnyExtension[] = [
  Markdown.configure({ html: true, breaks: true }),
  StarterKit.configure({
    heading: false,
    bulletList: {
      itemTypeName: 'listItem',
      keepMarks: false,
      keepAttributes: true,
    },
    hardBreak: { keepMarks: true },
    horizontalRule: { HTMLAttributes: { class: 'tyro-hr' } },
    orderedList: { itemTypeName: 'listItem' },
    link: false,
  }) as Extension,
  LinkWithState.configure({
    openOnClick: true,
    autolink: false,
    // Allow the `docs:space/page` custom scheme alongside http(s) so the
    // Tyrone docs-Q&A agent's source citations render as anchors. The
    // popover's onDocLinkClick handler intercepts clicks on those links
    // and opens the reader without navigating away.
    validate: (href) => /^(https?:\/\/|docs:)/.test(href),
  }),
  Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
  Highlight.configure({ multicolor: true }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  TextStyleKit,
  Image.configure({ inline: true }),
];

/**
 * Boots a read-only Tiptap editor pre-loaded with docPreviewExtensions and
 * syncs its content whenever the provided markdown changes.
 */
export function useDocumentationPreviewEditor(content: string): Editor | null {
  const editor = useEditor({
    extensions: docPreviewExtensions as Extensions,
    editable: false,
    content: '',
  });

  useEffect(() => {
    if (editor && content) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  return editor;
}
